import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface TrialContextType {
  hasTriedBefore: boolean;
  isFirstTimeUser: boolean;
  canTryNow: boolean;
  showRegistrationPrompt: boolean;
  markAsTriedOnce: () => void;
  startFirstTrial: () => void;
  hideRegistrationPrompt: () => void;
  isLoading: boolean;
}

const TrialContext = createContext<TrialContextType | undefined>(undefined);

export const useTrial = () => {
  const context = useContext(TrialContext);
  if (context === undefined) {
    throw new Error('useTrial must be used within a TrialProvider');
  }
  return context;
};

// Simple localStorage key to track if user has tried before
const TRIAL_STORAGE_KEY = 'manivo_has_tried_once';
const PROMPT_DISMISSED_KEY = 'manivo_registration_prompt_dismissed';

export const TrialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [hasTriedBefore, setHasTriedBefore] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [canTryNow, setCanTryNow] = useState(true);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check trial status on mount and when user changes
  useEffect(() => {
    checkTrialStatus();
  }, [user]);

  // Clear registration prompt when user logs in
  useEffect(() => {
    if (user) {
      setShowRegistrationPrompt(false);
    }
  }, [user]);

  const checkTrialStatus = useCallback(() => {
    // If user is logged in, they don't need trials
    if (user) {
      setCanTryNow(true);
      setHasTriedBefore(false);
      setIsFirstTimeUser(false);
      setShowRegistrationPrompt(false);
      return;
    }

    // Check if user has tried before
    const triedBefore = localStorage.getItem(TRIAL_STORAGE_KEY) === 'true';
    const promptDismissed = localStorage.getItem(PROMPT_DISMISSED_KEY) === 'true';
    
    setHasTriedBefore(triedBefore);
    setIsFirstTimeUser(!triedBefore);
    setCanTryNow(!triedBefore); // Can only try if they haven't tried before
    
    // Show registration prompt if they've tried before and haven't dismissed it
    if (triedBefore && !promptDismissed && !user) {
      setShowRegistrationPrompt(true);
    }
  }, [user]);

  const markAsTriedOnce = useCallback(() => {
    // Mark that user has tried Manivo once
    localStorage.setItem(TRIAL_STORAGE_KEY, 'true');
    setHasTriedBefore(true);
    setIsFirstTimeUser(false);
    setCanTryNow(false);
    
    // Show registration prompt after they've tried
    if (!user) {
      setTimeout(() => {
        setShowRegistrationPrompt(true);
      }, 2000); // Show prompt 2 seconds after they finish trying
    }
  }, [user]);

  const startFirstTrial = useCallback(() => {
    // This is called when user starts their first trial
    // The actual marking happens after they complete the manifestation
    setIsLoading(true);
    
    // Simulate a brief loading state
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const hideRegistrationPrompt = useCallback(() => {
    setShowRegistrationPrompt(false);
    // Remember that user dismissed the prompt (so we don't keep showing it)
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
  }, []);

  const value = {
    hasTriedBefore,
    isFirstTimeUser,
    canTryNow,
    showRegistrationPrompt,
    markAsTriedOnce,
    startFirstTrial,
    hideRegistrationPrompt,
    isLoading
  };

  return (
    <TrialContext.Provider value={value}>
      {children}
    </TrialContext.Provider>
  );
};